import React from 'react'

import { Box, Card, CardContent, CardHeader, Chip, LinearProgress, Link, Stack, Typography } from '@mui/material'
import { METER_LABELS, type ShowFilter } from 'features/customers/constants/contactsActivity'
import { useCustomerCreditSummary } from 'features/customers/hooks/useCustomerContactsActivity'

import type { Contacts } from '@albacross/types'

import LoadingView from 'components/LoadingView'

import { minutesBetween } from './requestOutcome'

type Tone = 'error' | 'warning' | 'info' | 'success'

type StateLine = { tone: Tone; text: string; show?: ShowFilter }

const METERS: Contacts.CreditMeter[] = ['email', 'phone']

// Only these; waiting has its own state line.
const COUNTED: [Contacts.RequestCategory, string][] = [
  ['charged', 'charged'],
  ['free', 'with free people'],
  ['refused', 'refused (no credits left)'],
  ['nothing_found', 'delivered nothing'],
  ['failed', 'failed or timed out'],
  ['late', 'delivered late'],
]

const TONE_COLORS: Record<Tone, string> = {
  error: 'error.main',
  warning: 'warning.dark',
  info: 'text.primary',
  success: 'success.dark',
}

const plural = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`

const ageOf = ({ oldestAt }: Contacts.Backlog, now: number) => (oldestAt ? minutesBetween(oldestAt, now) : 0)

const dayMonth = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })

const meterSentence = ({ used, limit }: Contacts.CreditUsage, reserved: number, enforced: boolean) => {
  const parts = [`${used} of ${limit} used`, `${Math.max(0, limit - used)} left`]
  if (enforced && used > limit) parts.push(`${used - limit} over the limit`)
  if (reserved > 0) parts.push(`${reserved} reserved right now`)
  return parts.join(', ')
}

const beforeSwitchLine = (summary: Contacts.CreditSummary, now: number): StateLine => {
  const counted = `${plural(summary.ledger.email.used, 'email')} and ${plural(summary.ledger.phone.used, 'phone')}`
  const { switchAt } = summary
  if (switchAt && Date.parse(switchAt) <= now) {
    return {
      tone: 'info',
      text: `Before the switch: used came from the old logs and nothing was refused. The ledger counted ${counted}.`,
    }
  }
  const until = switchAt ? `until ${dayMonth(switchAt)}` : 'yet'
  return {
    tone: 'info',
    text: `Not enforced ${until}: used comes from the old logs and nothing is refused. The ledger has counted ${counted}.`,
  }
}

const stateLines = (summary: Contacts.CreditSummary, now: number): StateLine[] => {
  const { stuck, slowJobs, waiting, refusesAll, source } = summary
  const lines: StateLine[] = []
  if (stuck.count === 1) {
    lines.push({
      tone: 'error',
      text: `1 request waiting for ${ageOf(stuck, now)} min: this should have expired. Check the sweep. Reading this tab expired it.`,
    })
  } else if (stuck.count > 1) {
    lines.push({
      tone: 'error',
      text: `${stuck.count} requests waiting past their expiry (oldest ${ageOf(stuck, now)} min): check the sweep. Reading this tab expired them.`,
    })
  }
  if (slowJobs.count === 1) {
    lines.push({ tone: 'warning', text: `1 provider job running for ${ageOf(slowJobs, now)} min.` })
  } else if (slowJobs.count > 1) {
    lines.push({
      tone: 'warning',
      text: `${slowJobs.count} provider jobs running for over 15 min (oldest ${ageOf(slowJobs, now)} min).`,
    })
  }
  for (const meter of METERS) {
    if (!refusesAll[meter]) continue
    const refused = source === 'ledger' ? 'every request is refused' : 'from the switch every request is refused'
    lines.push({ tone: 'error', text: `No ${meter} credits on this plan (limit 0): ${refused}.` })
  }
  if (waiting.count) {
    lines.push({
      tone: 'warning',
      text: `${plural(waiting.count, 'request')} waiting (oldest ${ageOf(waiting, now)} min).`,
      show: 'waiting',
    })
  } else if (!stuck.count) {
    lines.push({ tone: 'success', text: 'Nothing waiting.' })
  }
  if (source === 'logs') lines.push(beforeSwitchLine(summary, now))
  return lines
}

const Meter = ({ meter, summary }: { meter: Contacts.CreditMeter; summary: Contacts.CreditSummary }) => {
  const usage = summary[meter]
  const value = usage.limit > 0 ? Math.min(100, (usage.used / usage.limit) * 100) : 0
  return (
    <Box
      data-testid={`contacts-meter-${meter}`}
      sx={{ display: 'flex', alignItems: 'center', columnGap: 2, rowGap: 0.5, flexWrap: 'wrap' }}>
      <Typography sx={{ width: 64 }} variant="body2">
        {METER_LABELS[meter]}
      </Typography>
      <LinearProgress
        aria-label={`${METER_LABELS[meter]} used`}
        color={usage.used >= usage.limit ? 'error' : 'primary'}
        sx={{ flex: '1 1 120px', maxWidth: 320, height: 8, borderRadius: 1 }}
        value={value}
        variant="determinate"
      />
      <Typography sx={{ flex: '1 1 220px' }} variant="body2">
        {meterSentence(usage, summary.ledger[meter].reserved, summary.source === 'ledger')}
      </Typography>
    </Box>
  )
}

type ContactsAtAGlanceProps = { period: string; show: ShowFilter; onShow: (show: ShowFilter) => void }

const ContactsAtAGlance = ({ period, show, onShow }: ContactsAtAGlanceProps) => {
  const { data: summary, isLoading, isError } = useCustomerCreditSummary(period)
  const now = Date.now()

  const countChip = (value: ShowFilter, label: string) => (
    <Chip
      aria-pressed={show === value}
      clickable
      color={show === value ? 'primary' : 'default'}
      component="button"
      key={value}
      label={label}
      onClick={() => onShow(value)}
      size="small"
      variant={show === value ? 'filled' : 'outlined'}
    />
  )

  return (
    <Card component="section" aria-label="At a glance" data-testid="contacts-at-a-glance" variant="outlined">
      <CardHeader
        disableTypography
        title={
          <Typography component="h3" variant="h6">
            {period} at a glance
          </Typography>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <LoadingView error={isError ? 'Could not load the month' : undefined} loading={isLoading}>
          {summary && (
            <Stack spacing={1.5}>
              {METERS.map(meter => (
                <Meter key={meter} meter={meter} summary={summary} />
              ))}
              <Stack
                aria-label="Requests by outcome"
                direction="row"
                sx={{ flexWrap: 'wrap', alignItems: 'center' }}
                spacing={1}
                useFlexGap>
                {summary.requests.all ? (
                  [
                    countChip('all', plural(summary.requests.all, 'request')),
                    ...COUNTED.filter(([category]) => summary.requests[category] > 0).map(([category, label]) =>
                      countChip(category, `${summary.requests[category]} ${label}`),
                    ),
                  ]
                ) : (
                  <Typography variant="body2">No requests.</Typography>
                )}
              </Stack>
              <Box data-testid="contacts-state">
                {stateLines(summary, now).map(({ tone, text, show: target }) => (
                  <Typography color={TONE_COLORS[tone]} key={text} variant="body2">
                    {target ? (
                      <Link color="inherit" component="button" onClick={() => onShow(target)} variant="body2">
                        {text}
                      </Link>
                    ) : (
                      text
                    )}
                  </Typography>
                ))}
              </Box>
            </Stack>
          )}
        </LoadingView>
      </CardContent>
    </Card>
  )
}

export default ContactsAtAGlance
